import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { useCallback, useRef, useState } from 'react';
import NotFound from './Layout/NotFound';
import { BrowserRouter, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle, StyleSheetManager, ThemeProvider } from 'styled-components';
import useAuthService, { AuthServiceProvider } from '../Hooks/UseAuthService';
import { useEffect } from 'react';
import { fetchAndParseConfiguration } from '../Config/Configuration';
import useConfiguration, { ConfigurationContext } from '../Hooks/UseConfiguration';
import { BlankPage } from './Layout/Page';
import { useTheme } from '../Hooks/UseThemeStore';
import { GlobalRoutes, HomeShowOrgChooserQueryParam } from '../Utils/NavigationUtils';
import '../Assets/AppSkeleton.css';
import Logout from './General/Logout';
import { BinaryTheme } from '../Stores/ThemeStore';
import GoogleTagManager from './GoogleTagManager';
import { HelmetProvider } from 'react-helmet-async';
import { UploadManagerProvider } from '@cb/solaris-react/Hooks/UseUploadManager';
import { FetchClientContext } from '@cb/solaris-react/Utility/FetchClient';
import { useObjectStorageUploadManager } from '../Utils/ObjectStorageUploadManager';
import useAuthenticatedFetchClient from '../Hooks/UseAuthenticatedFetchClient';
import LoadingSpinner from '@cb/solaris-react/Components/Loading/LoadingSpinner';
import Button from '@cb/solaris-react/Components/Interactive/Button/Button';
import { createScrollbar } from '@cb/solaris-react/Constants/Scrollbars';
import SolarisThemeProvider from '@cb/solaris-react/Components/Utility/SolarisThemeProvider';
import JoyrideContainer from '@cb/solaris-react/Components/Interactive/Joyride/JoyrideContainer';
import ContextMenuContainer from '@cb/solaris-react/Components/Interactive/ContextMenu/ContextMenuContainer';
import ToastContainer from '@cb/solaris-react/Components/Interactive/Toast/ToastContainer';
import ModalContainer from '@cb/solaris-react/Components/Interactive/Modal/ModalContainer';
import ErrorMessage from '@cb/solaris-react/Components/Content/ErrorMessage';
import { IsValidReactNode } from '@cb/solaris-react/Utility/ReactUtils';
import { useTokenData, useTokenStore } from '../Hooks/UseTokenStore';
import useThemeStore from '../Hooks/UseThemeStore';
import MaterialIconPreload from '@cb/solaris-react/Components/Content/MaterialIconPreload';
export default function AppSkeleton(props) {
    const { loadingConditions, defaultPageTitle, children } = props;
    const [configuration, setConfiguration] = useState();
    const [error, setError] = useState();
    const fetchPromise = useRef();
    useEffect(() => {
        if (fetchPromise.current) {
            return;
        }
        fetchPromise.current = props.fetchAndParseConfiguration
            ? props.fetchAndParseConfiguration()
            : fetchAndParseConfiguration();
        fetchPromise.current
            .then(setConfiguration)
            .catch((err) => {
            setError(err);
        })
            .finally(() => {
            fetchPromise.current = undefined;
        });
    }, [props, props.fetchAndParseConfiguration]);
    const themeStore = useThemeStore();
    const { theme, isThemeLoading } = useTheme();
    useEffect(() => {
        if (!isThemeLoading && !theme) {
            // This is an async method, but we're not going to wait for it (since it will trigger a re-render when it's done anyway)
            themeStore
                .loadThemeUsingPreferences(props.defaultToLightTheme ? BinaryTheme.LIGHT : BinaryTheme.DARK)
                .catch((err) => {
                throw err;
            });
        }
        const onKeyPress = (e) => {
            if (e.code === 'KeyT' && e.ctrlKey && e.altKey) {
                e.stopImmediatePropagation();
                e.stopPropagation();
                themeStore.toggleTheme();
            }
        };
        document.addEventListener('keyup', onKeyPress);
        return () => {
            document.removeEventListener('keyup', onKeyPress);
        };
    }, [isThemeLoading, props.defaultToLightTheme, theme, themeStore]);
    const tokenStore = useTokenStore();
    useEffect(() => {
        tokenStore.ensureLoaded().catch((err) => {
            throw err;
        });
    }, [tokenStore]);
    if (!theme) {
        return null;
    }
    if (!configuration || loadingConditions?.filter((x) => x.condition).length) {
        let loadingText = 'Loading';
        if (!configuration) {
            loadingText = 'Loading application configuration';
        }
        else {
            const condition = loadingConditions?.filter((x) => x.condition).length
                ? loadingConditions.filter((x) => x.condition)[0]
                : undefined;
            loadingText = condition && condition.message ? condition.message : loadingText;
        }
        return (_jsx(ThemeProvider, { theme: theme, children: _jsx(BlankPage, { pageTitle: defaultPageTitle, children: error ? (_jsx(ErrorMessage, { title: "Failed to load configuration", overlay: true, children: error.message })) : (_jsx(LoadingSpinner, { text: loadingText, overlay: true })) }) }));
    }
    return (_jsx(StyleSheetManager, { shouldForwardProp: shouldForwardProp, children: _jsx(SolarisThemeProvider, { theme: theme, children: _jsx(ConfigurationContext.Provider, { value: configuration, children: _jsx(AuthServiceProvider, { children: _jsxs(HelmetProvider, { children: [_jsx(GoogleTagManager, {}), _jsx(AppSkeletonRouter, { ...props, children: children })] }) }) }) }) }));
}
// Always forward all properties on a styled-component to the underlying DOM element/react component
function shouldForwardProp() {
    return true;
}
function AppSkeletonRouter(props) {
    const { children, authenticatedRoutes, unauthenticatedRoutes, notFoundComponent, defaultPageTitle } = props;
    const configuration = useConfiguration();
    const fetchClient = useAuthenticatedFetchClient();
    const objectStorageUploadManager = useObjectStorageUploadManager({
        filePathPrefix: configuration.objectStorageConfig.filePathPrefix ?? '',
    });
    return (_jsx(FetchClientContext.Provider, { value: fetchClient, children: _jsx(UploadManagerProvider, { uploadManager: objectStorageUploadManager, children: _jsxs(BrowserRouter, { children: [_jsxs(Switch, { children: [_jsx(Route, { exact: true, path: GlobalRoutes.Oauth2Callback, children: _jsx(OauthCallback, { defaultPageTitle: defaultPageTitle }) }), _jsx(Route, { exact: true, path: GlobalRoutes.Logout, children: unauthenticatedRoutes?.find((route) => route.path === GlobalRoutes.Logout)?.component ?? (_jsx(Logout, { defaultPageTitle: defaultPageTitle })) }), authenticatedRoutes?.map((route) => (_jsx(AuthenticatedRoute, { exact: route.exact, path: route.path, defaultPageTitle: route.defaultPageTitle || defaultPageTitle, children: route.component }, route.path))), unauthenticatedRoutes?.map((route) => (_jsx(Route, { exact: route.exact, path: route.path, children: route.component }, route.path))), _jsx(Route, { component: notFoundComponent || NotFound })] }), children, _jsx(JoyrideContainer, {}), _jsx(ContextMenuContainer, {}), _jsx(ToastContainer, {}), _jsx(ModalContainer, {}), _jsx(GlobalScrollbar, {}), _jsx(MaterialIconPreload, {})] }) }) }));
}
function AuthenticatedRoute(props) {
    const authService = useAuthService();
    const { tokenData, isTokenLoading } = useTokenData();
    const location = useLocation();
    const config = useConfiguration();
    const { children, ...remainingProps } = props;
    if (isTokenLoading) {
        return _jsx(LoadingSpinner, { overlay: true });
    }
    const renderRoute = () => {
        if (tokenData) {
            if (IsValidReactNode(children)) {
                return children;
            }
            return null;
        }
        const callbackUri = `${window.location.protocol}//${window.location.host}${GlobalRoutes.Oauth2Callback}`;
        const afterLoginRedirectLocation = {
            pathname: location.pathname,
            search: location.search,
        };
        authService
            .initialiseOauth2Flow(afterLoginRedirectLocation, config.oauth2Config.issuer, callbackUri)
            .catch((err) => {
            throw err;
        });
        return _jsx(BlankPage, { pageTitle: props.defaultPageTitle });
    };
    return _jsx(Route, { ...remainingProps, render: renderRoute });
}
const OauthCallback = (props) => {
    const authService = useAuthService();
    const history = useHistory();
    const { setTokenData } = useTokenData();
    const [failed, setFailed] = useState(false);
    const promiseRef = useRef();
    const redirectHomeToRestartLoginProcess = () => history.replace('/');
    const handleCallbackUrlFragment = useCallback(async () => {
        try {
            const result = await authService.handleCallbackUrlFragment();
            const { state, ...tokenData } = result;
            setTokenData(tokenData);
            if (state) {
                // Try parse into location
                try {
                    const location = state;
                    history.replace(location);
                    return;
                }
                catch (err) {
                    console.error('Could not parse oauth2 state for redirect location, proceeding to default location.', err);
                }
            }
            else {
                console.error('No state');
            }
            history.replace(`/?${HomeShowOrgChooserQueryParam}=true`);
        }
        catch (err) {
            console.error(err);
            setTokenData(null);
            setFailed(true);
        }
    }, [authService, history, setTokenData]);
    useEffect(() => {
        if (promiseRef.current) {
            return;
        }
        promiseRef.current = handleCallbackUrlFragment();
    }, [authService, handleCallbackUrlFragment, history, setTokenData]);
    if (failed) {
        return (_jsx(BlankPage, { pageTitle: props.defaultPageTitle, children: _jsxs(StyledSignInErrorContainer, { children: [_jsx("h2", { children: "Sign-in Error" }), _jsx("p", { children: "An error occurred during the sign on process, contact support if the error persists" }), _jsx(Button, { onClick: redirectHomeToRestartLoginProcess, children: "Try Again" })] }) }));
    }
    return _jsx(BlankPage, { pageTitle: props.defaultPageTitle });
};
const StyledSignInErrorContainer = styled.div `
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	h2 {
		font-size: calc(${(props) => props.theme.typography.xl} * 3);
		font-weight: 700;
		margin-top: 0;
		margin-bottom: calc(${(props) => props.theme.spacing.md} * 2);
	}
	p {
		margin-bottom: ${(props) => props.theme.spacing.xl};
	}
`;
const GlobalScrollbar = createGlobalStyle `
	html {
		${(props) => createScrollbar({
    background: props.theme.palette.shade6,
    thumb: props.theme.secondary,
    thickness: 10,
})}
	}
`;
//# sourceMappingURL=AppSkeleton.js.map